import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { Box, styled, Typography } from '@mui/material';

import { useBreadcrumbs } from 'Breadcrumb';
import { getCourse, getFreshStartDesktop, getMyDesktop, useSelectCourse } from 'Courses/data';
import {
  getActiveParticipantsCount,
  getExpectedParticipantsCount,
  getJoinedParticipantsCount,
  isCourseInSession
} from 'Courses/utils/participants-utils';
import { urls } from 'Router';
import { useAppDispatch } from 'Store';
import { Typo } from 'Themes/typo';

import { breadcrumbs as coursesBreadcrumbs } from '../courses-list/courses-page';

import { FreshStartSettingsForm } from './fresh-start-settings-form';
import { Settings } from './settings';

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
`;

const HeaderContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

export const CourseSettingsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useAppDispatch();

  const course = useSelectCourse(id ?? '');

  const showParticipantsInfo = course && isCourseInSession(course);
  const activeCount = showParticipantsInfo ? getActiveParticipantsCount(undefined, course) : 0;
  const joinedCount = showParticipantsInfo ? getJoinedParticipantsCount(undefined, course) : 0;
  const expectedCount = course ? getExpectedParticipantsCount(course) : 0;

  const breadcrumbLabel =
    course && showParticipantsInfo
      ? `${course.title} (${activeCount}/${joinedCount}/${expectedCount})`
      : course?.title;

  const items = [
    ...coursesBreadcrumbs,
    {
      label: breadcrumbLabel,
      to: urls.coursePage(id as string)
    },
    { translateKey: 'coursePanel.courseSettings', to: '/' }
  ];

  useBreadcrumbs(items);

  useEffect(() => {
    if (!id) {
      return;
    }

    if (!course) {
      dispatch(getCourse(id));
    }

    if (course && course.myDesktop === undefined) {
      dispatch(getMyDesktop({ courseId: id }));
    }
    if (course && course.freshStartDesktop === undefined) {
      dispatch(getFreshStartDesktop({ courseId: id }));
    }
  }, [id, course, dispatch]);

  if (!id) {
    return <div>Invalid course ID</div>;
  }

  return (
    <Container>
      {course ? (
        <>
          <HeaderContainer>
            <Typography variant={Typo.title}>
              {!course?.hasFreshStart
                ? t('coursePanel.freshStartSettings')
                : t('coursePanel.courseSettings')}
            </Typography>
          </HeaderContainer>

          {!course?.hasFreshStart && course?.isTrainer ? (
            <FreshStartSettingsForm course={course} />
          ) : (
            <Settings course={course} />
          )}
        </>
      ) : (
        'loading'
      )}
    </Container>
  );
};
